import React from 'react';
import {
  Shield, Server, ScrollText, Fingerprint, FileLock, Gauge, Eye,
  UserCheck, Building2,
} from 'lucide-react';
import { ENTITY } from '@/lib/entity';

/* ── Institutional Infrastructure ──────────────────────────────────
 * A restrained, fact-first credibility layer for /about.
 * Every bullet below corresponds to a concrete capability implemented
 * in the platform (no aspirational claims):
 *   - NDA-gated deal rooms       → DealAccess/NDA gating across /app/backend/routes
 *   - Permissioned documents     → DocumentsTab + document-request flow
 *   - Immutable audit trail      → activity routes + audit collection
 *   - Controlled circulation     → invites/participants admin UI
 *   - 2FA + Passkey auth         → auth_sessions + WebAuthn
 *   - AWS document storage       → private S3 document bucket
 *   - Daily FX-normalised stats  → /api/public/stats cached nightly
 *   - Curated counterparty access→ Counterparty curation in CRM
 */
const PILLARS = [
  {
    title: 'Access control',
    icon: Shield,
    items: [
      { icon: Shield, text: 'NDA-gated deal rooms with per-counterparty visibility.' },
      { icon: UserCheck, text: 'Role-based access for admins, internal team, lenders and borrowers.' },
      { icon: Eye, text: 'Admin-controlled deal circulation — counterparties see only what they are invited to see.' },
    ],
  },
  {
    title: 'Document protection',
    icon: FileLock,
    items: [
      { icon: FileLock, text: 'Private document storage with permissioned access and expiring download links.' },
      { icon: ScrollText, text: 'Full audit trail of document views, downloads, version changes and workflow events.' },
      { icon: Building2, text: 'Counterparty-specific workspaces keep borrower and lender-side material segregated.' },
    ],
  },
  {
    title: 'Account security',
    icon: Fingerprint,
    items: [
      { icon: Fingerprint, text: 'Two-factor authentication across all internal and counterparty accounts.' },
      { icon: Fingerprint, text: 'Passkey-capable sign-in (Face ID, Touch ID, platform authenticators) for modern devices.' },
      { icon: UserCheck, text: 'Trusted-device management with revocable sessions and activity timestamps.' },
    ],
  },
  {
    title: 'Platform reliability',
    icon: Server,
    items: [
      { icon: Server, text: 'Private document storage uses Amazon Web Services (AWS) S3.' },
      { icon: Gauge, text: 'Daily refreshed aggregate pipeline reporting normalised into AUD.' },
      { icon: ScrollText, text: 'Immutable activity log capturing every material action across the deal lifecycle.' },
    ],
  },
];

export const InstitutionalInfrastructure = () => (
  <section className="py-20 bg-secondary/30" data-testid="institutional-infrastructure-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-3xl mb-12">
        <p className="font-inter text-[11px] text-[var(--gpc-ink-soft)] uppercase tracking-wider mb-3">
          Institutional Infrastructure
        </p>
        <h2 className="font-grotesk text-2xl font-semibold text-foreground mb-3">
          Built for confidentiality, auditability and institutional fit
        </h2>
        <p className="font-inter text-sm text-muted-foreground leading-relaxed">
          Every transaction on the Capital Access Platform follows the same controlled workflow.
          Information is gated, activity is logged, and counterparty access is curated. The platform is
          operated by GP Capital; no third party touches confidential transaction data.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {PILLARS.map((p) => {
          const HeadIcon = p.icon;
          return (
            <div
              key={p.title}
              className="bg-background border rounded-lg p-6"
              data-testid={`infra-pillar-${p.title.toLowerCase().replace(/\s+/g,'-')}`}
            >
              <div className="flex items-center gap-2.5 mb-4 pb-3 border-b">
                <div className="w-8 h-8 rounded-md bg-teal/10 flex items-center justify-center shrink-0">
                  <HeadIcon className="w-4 h-4 text-teal" />
                </div>
                <h3 className="font-grotesk text-sm font-semibold text-foreground">{p.title}</h3>
              </div>
              <ul className="space-y-2.5">
                {p.items.map((it, i) => {
                  const ItemIcon = it.icon;
                  return (
                    <li key={i} className="flex items-start gap-2.5">
                      <ItemIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                      <span className="font-inter text-xs text-foreground/80 leading-relaxed">{it.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Entity clarity strip — quiet, single-line facts only */}
      <div
        className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t"
        data-testid="entity-clarity-strip"
      >
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
            Operating Entity
          </p>
          <p className="font-grotesk text-sm font-medium text-foreground">
            {ENTITY.legalName}
          </p>
          <p className="font-inter text-[11px] text-muted-foreground">
            ACN {ENTITY.acn} · ABN {ENTITY.abn}
          </p>
        </div>
        <div className="sm:text-right">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
            Intended Audience
          </p>
          <p className="font-inter text-xs text-foreground/80 max-w-sm">
            Private Capital is for wholesale / sophisticated counterparties only. Public Private Capital information does not constitute an offer of securities.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default InstitutionalInfrastructure;
